<template>
    <header class="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50 shadow-sm">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div class="flex items-center justify-between">
                <!-- Logo -->
                <router-link :to="homeUrl" class="inline-flex items-center">
                    <img src="/logo-light.svg" alt="Regula Data" width="200" height="32" />
                </router-link>

                <!-- Desktop Navigation - exact React: text-muted-foreground hover:text-foreground transition-colors uppercase text-sm tracking-wide font-medium -->
                <nav class="hidden md:flex items-center gap-8">
                    <a
                        v-for="item in navItems"
                        :key="item.id"
                        :href="`#${item.id}`"
                        class="text-muted-foreground hover:text-foreground transition-colors uppercase text-sm tracking-wide font-medium"
                        @click.prevent="handleNavClick(item.id)"
                    >
                        {{ $t(`header.${item.key}`) }}
                    </a>
                    <router-link
                        :to="blogUrl"
                        class="text-muted-foreground hover:text-foreground transition-colors uppercase text-sm tracking-wide font-medium"
                    >
                        {{ $t('header.blog') }}
                    </router-link>
                </nav>

                <!-- Actions - responsive button sizing and spacing for mobile -->
                <div class="flex items-center gap-2 sm:gap-4">
                    <LanguagePicker class="hidden sm:block" />
                    <a
                        :href="appUrl"
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        class="hidden md:inline-flex items-center text-muted-foreground hover:text-foreground transition-colors uppercase text-sm tracking-wide font-medium"
                    >
                        {{ $t('header.login') }}
                    </a>
                    <button
                        class="bg-accent text-accent-foreground px-4 py-2 sm:px-5 rounded-lg hover:bg-accent/90 transition-all uppercase text-xs sm:text-sm tracking-wide font-medium"
                        @click="handleCtaClick"
                    >
                        <span class="hidden xs:inline">{{ $t('hero.cta') }}</span>
                        <span class="xs:hidden">{{ $t('hero.ctaShort') }}</span>
                    </button>
                    <MobileNav
                        class="md:hidden"
                        :navigation-items="mobileNavItems"
                        :login-url="appUrl"
                        @navigate="handleNavClick"
                    />
                </div>
            </div>
        </div>
    </header>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LanguagePicker from '@/components/molecules/LanguagePicker.vue';
import MobileNav from '@/components/molecules/MobileNav.vue';

export default defineComponent({
    name: 'Header',
    components: {
        LanguagePicker,
        MobileNav
    },
    setup() {
        const { t } = useI18n();
        const route = useRoute();
        const router = useRouter();

        const currentLocale = computed(() => {
            const routeLocale = route.params.locale as string;
            return (routeLocale === 'en' || routeLocale === 'lt') ? routeLocale : 'en';
        });

        const homeUrl = computed(() => `/${currentLocale.value}`);
        const blogUrl = computed(() => `/${currentLocale.value}/blog`);

        const navItems = [
            { id: 'how-it-works', key: 'howItWorks' },
            { id: 'pricing', key: 'pricing' }
        ];

        const mobileNavItems = computed(() => [
            ...navItems.map(item => ({
                id: item.id,
                label: t(`header.${item.key}`)
            })),
            {
                id: `__route:${blogUrl.value}`,
                label: t('header.blog')
            }
        ]);

        const isHomePage = computed(() => {
            const p = route.path;
            return p === `/${currentLocale.value}` || p === '/';
        });

        const handleNavClick = (id: string) => {
            if (isHomePage.value) {
                const element = document.getElementById(id);
                if (element) {
                    const headerOffset = 80;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
            } else {
                void router.push(`/${currentLocale.value}#${id}`);
            }
        };

        const handleCtaClick = () => {
            handleNavClick('contact');
        };

        const appUrl = import.meta.env.VITE_APP_URL || 'https://app.reguladata.lt';

        return {
            navItems,
            mobileNavItems,
            homeUrl,
            blogUrl,
            handleNavClick,
            handleCtaClick,
            appUrl
        };
    }
});
</script>
